// @flow
import React from 'react'
import Image from 'ol/layer/Image'
import ImageArcGISrest from 'ol/source/ImageArcGISRest'
import { unByKey } from 'ol/Observable'
import Er2Map from 'catena/er2_map/src/map'
import { getMapserver } from '../utils'
import MapsourcesPanel from '../components/mapsources'
import type { SitesType } from '../reducers/public_sites'
import type { ActionsType, MapType } from '../reducers/map'
import type { MapsourcesType } from '../reducers/mapsources'

type PropsType = {
    active: boolean,
    er2Map: Er2Map,
    map: MapType,
    mapserverHost: string,
    mapsourceIsLoading: Function,
    mapsources: MapsourcesType,
    mask: 'layers' | 'tables',
    onAddPublicServices: Function,
    onUpdateGroupLayerOrdering: Function,
    publicSites: SitesType,
    server: string,
    updateMapsourceOrder: Function,
    ...ActionsType,
}

type StateType = {
    loadingTileCounts: {},
}

// eslint-disable-next-line
class MapsourcesContainer extends React.PureComponent<PropsType> {
    constructor(props: PropsType) {
        super(props)
        this.state = {
            loadingTileCounts: {},
        }
    }
    componentDidMount() {
        this.initializeMapsources()

        // Reattach layer listeners
        this.props.mapsources.mapsources.forEach((m) => {
            if (m.type === 'arcgisrest') {
                const name = this.getMapsourceLayerName(m)
                const layer = this.getLayerByName(name)
                if (layer) {
                    this.addLayerListeners(m, layer)
                }
            }
        })
    }

    /**
     * Update state from new props
     * @param nextprops
     */
    componentWillReceiveProps(nextprops: PropsType) {
        this.updateMapsources(nextprops)
    }

    componentWillUnmount() {
        // Remove listeners on layers
        this.props.mapsources.mapsources.forEach((m) => {
            if (m.type === 'arcgisrest') {
                const name = this.getMapsourceLayerName(m)
                const layer = this.getLayerByName(name)
                if (layer) {
                    this.removeLayerListeners(layer)
                }
            }
        })
    }

    /**
     * Add or update the openlayers layers for each mapsource.
     */
    initializeMapsources() {
        const mapserver = getMapserver(this.props.mapsources)
        this.props.er2Map.setUserMapfile(mapserver.mapfile, {
            visibleLayerNames: mapserver.layers.slice().reverse().filter(l => l.visible).map(l => l.name),
        })

        // Update public services
        this.props.mapsources.mapsources.forEach((m) => {
            if (m.type === 'arcgisrest') {
                this.updateLayer(m, {
                    opacity: m.opacity,
                })
            }
        })
    }

    /**
     * Add, delete, or refresh openlayers layers from mapsource info
     * @param nextprops
     */
    updateMapsources(nextprops: PropsType) {
        if (nextprops.mapsources.dirty && nextprops.er2Map.olmap) {
            const mapserver = getMapserver(nextprops.mapsources)
            this.props.er2Map.setUserMapfile(mapserver.mapfile, {
                visibleLayerNames: mapserver.layers.slice().reverse().filter(l => l.visible).map(l => l.name),
            })
        }

        if (nextprops.mapsources !== this.props.mapsources) {
            this.checkPublicMapsources(nextprops)
        }
    }

    addLayerListeners(arcgisrestSource, layer) {
        const source = layer.getSource()
        if (!source.get('imageloadstartkey')) {
            const imageloadstartkey = source.on('imageloadstart', (e) => {
                this.mapsourceIsLoading(arcgisrestSource, true)
            })
            const imageloadendkey = source.on('imageloadend', (e) => {
                this.mapsourceIsLoading(arcgisrestSource, false)
            })
            source.set('imageloadstartkey', imageloadstartkey)
            source.set('imageloadendkey', imageloadendkey)
        } else {
            // Shouldn't be an error?
            console.warn('tried to add duplicate listener')
        }
    }

    removeLayerListeners(layer) {
        const source = layer.getSource()
        unByKey(source.get('imageloadstartkey'))
        unByKey(source.get('imageloadendkey'))
        source.unset('imageloadstartkey')
        source.unset('imageloadendkey')
    }

    /**
     * Check if the public mapsource properties have changed.
     * @param newprops
     */
    checkPublicMapsources(newprops: PropsType) {
        const oldprops = this.props

        // Check for deleted services
        if (oldprops.mapsources) {
            oldprops.mapsources.mapsources.forEach((m) => {
                if (m.type === 'arcgisrest' && !newprops.mapsources.mapsources.find(mNew => mNew.name === m.name)) {
                    const layer = this.getLayerByName(this.getMapsourceLayerName(m))
                    this.props.er2Map.olmap.removeLayer(layer)
                }
            })
        }
        // Check for modified mapsources
        newprops.mapsources.mapsources.forEach((m) => {
            const oldmapsource = oldprops.mapsources.mapsources.find(mOld => mOld.name === m.name)
            // if oldmapsource doesn't exist or is a new object
            if (oldmapsource !== m) {
                if (m.type === 'arcgisrest') {
                    this.updateLayer(m, {
                        opacity: m.opacity,
                    })
                }
            }
        })
    }

    getLayerByName(layerLabel: string) {
        const flayers = this.props.er2Map.olmap.getLayers().getArray().filter(l => l.get('label') === layerLabel)
        if (flayers.length) return flayers[0]
        console.warn(`getLayerByName: Cannot find layer named ${layerLabel}`)
        return null
    }

    /**
     * Get an identifier for the openlayers layer associated with the given mapsource.
     * @param mapsource
     * @returns {string}
     */
    getMapsourceLayerName(mapsource: UserMapsourceType) {
        if (mapsource.type === 'arcgisrest') {
            return `${mapsource.site_name}-${mapsource.service_name}`
        } else if (mapsource.mapfile) {
            return mapsource.mapfile
        }
        return null
    }

    /**
     * Update or create a WMS layer with an arcgisrest source
     */
    updateLayer(mapsource, params) {
        const name = this.getMapsourceLayerName(mapsource)
        let layer = this.getLayerByName(name)
        if (!layer) {
            layer = this.addLayer(mapsource, name)
        }
        this.refreshLayer(layer, mapsource, params)
        return layer
    }

    addLayer(mapsource, name) {
        const url = `${mapsource.url}${mapsource.service_name}/MapServer/`
        const source = new ImageArcGISrest({
            ratio: 1,
            url,
        })
        const layer = new Image({
            source,
            label: name,
            mapsource_type: 'arcgisrest',
            zIndex: 0,
        })
        this.addLayerListeners(mapsource, layer)
        this.props.er2Map.olmap.addLayer(layer)
        return layer
    }

    refreshLayer(layer, mapsource, params) {
        layer.getSource().updateParams({
            layers: `show:${mapsource.layers.filter(arcLayer => arcLayer.visible).map(arcLayer => arcLayer.id).join(',')}`,
        })
        layer.setVisible(mapsource.visible)
        if (params) {
            layer.setOpacity(params.opacity / 100.0)
        }
    }

    mapsourceIsLoading(mapsource, isLoading) {
        const { loadingTileCounts } = this.state
        if (loadingTileCounts[mapsource.name] === undefined) {
            loadingTileCounts[mapsource.name] = 0
        }
        loadingTileCounts[mapsource.name] += isLoading ? 1 : -1
        if (loadingTileCounts[mapsource.name] < 0) loadingTileCounts[mapsource.name] = 0
        this.setState({ loadingTileCounts })
    }

    render() {
        if (this.props.active) {
            return (
                <MapsourcesPanel
                    {...this.props}
                    loadingTileCounts={this.state.loadingTileCounts}
                    mapsources={this.props.mapsources}
                    mask={this.props.mask}
                />
            )
        }
        return null
    }
}

export default MapsourcesContainer
